import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

/** Where the panel ended up, in viewport coordinates. */
interface Placement {
  left: number;
  /** The anchor's width, which the panel takes as its floor. */
  minWidth: number;
  top?: number;
  bottom?: number;
}

/**
 * Positions a panel against the control that opened it.
 *
 * Every dropdown in the admin app used to hang off its trigger with
 * `absolute top-[calc(100%+4px)]`, which works right up until the trigger sits
 * inside something that clips — and three of ours do: the modal body scrolls,
 * `ui/table` scrolls sideways, and card headers clip their overflow. The panel
 * was cut off in all three.
 *
 * So the panel is rendered through a portal and positioned against the
 * viewport instead. That buys correctness at the cost of having to re-place it
 * whenever anything underneath moves, which is what the listeners below do.
 *
 * Returns the refs to attach, plus the `style` and gap class the panel wears.
 */
export function useAnchoredPanel<A extends HTMLElement, P extends HTMLElement>(open: boolean) {
  const anchor = useRef<A>(null);
  const panel = useRef<P>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  /**
   * It opens downward unless the list genuinely does not fit and there is more
   * room above — a trigger low on a long page should still open down if the
   * list is short enough to clear the fold. The panel may grow wider than its
   * anchor for a long label, so the left edge is clamped to keep that growth
   * on screen.
   */
  const position = useCallback(() => {
    const el = anchor.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const panelHeight = panel.current?.offsetHeight ?? 0;
    // The panel is never narrower than its anchor, so the clamp has to reckon
    // with whichever of the two is wider.
    const panelWidth = Math.max(panel.current?.offsetWidth ?? 0, rect.width);
    const below = window.innerHeight - rect.bottom;
    const flip = panelHeight > below && rect.top > below;

    setPlacement({
      left: Math.max(0, Math.min(rect.left, window.innerWidth - panelWidth)),
      minWidth: rect.width,
      ...(flip ? { bottom: window.innerHeight - rect.top } : { top: rect.bottom }),
    });
  }, []);

  // Before paint, so the panel is never shown at a stale position.
  useLayoutEffect(() => {
    if (open) position();
  }, [open, position]);

  // Anything that moves the anchor moves the panel. Capture, because the
  // scroll that matters is usually an ancestor's rather than the window's.
  useEffect(() => {
    if (!open) return;
    const reposition = () => position();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, position]);

  const flipped = placement?.bottom !== undefined;

  const style: CSSProperties = {
    position: 'fixed',
    left: placement?.left ?? 0,
    top: placement?.top,
    bottom: placement?.bottom,
    minWidth: placement?.minWidth,
    // The first pass has nothing to measure. Hiding it costs nothing — the
    // corrected pass still lands before the same paint.
    visibility: placement ? 'visible' : 'hidden',
  };

  /** The air between anchor and panel, on whichever side it opened. */
  const gapClass = flipped ? 'mb-(--menu-gap)' : 'mt-(--menu-gap)';

  return { anchor, panel, style, gapClass };
}

/** The shared skin for a dropdown panel: the surface, not the rows inside it. */
export const menuPanelCls =
  'z-(--z-popover) m-0 max-h-(--menu-max-height) max-w-(--menu-max-width) list-none overflow-y-auto rounded-xl border border-menu-edge bg-menu-bg p-1 text-menu-fg shadow-lift';

/** The shared skin for one row. `active` is where the cursor is, `selected` is the value. */
export const menuOptionCls =
  'flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-base transition-colors';
